// Versions-Check: erkennt, wenn auf dem Server ein NEUES App-Bundle deployt
// wurde, während der Client noch ein altes (gecachtes) Bundle ausführt.
//
// Hintergrund: app.redcrypto.de ist eine PWA ohne App-Shell-Service-Worker.
// index.html ist no-cache, die gehashten /static-Bundles sind immutable. Bei
// einem RELOAD bekommt der Client also immer das neue Bundle. Problem: eine offene
// PWA / ein offener Tab (typisch auf dem Handy) lädt NIE neu → rechnet weiter mit
// der alten FIFO-Logik → Mobile zeigt andere Zahlen als der frisch geladene
// Desktop. Genau die heutige Mobile/Web-Divergenz.
//
// Lösung ohne Build-Config: der Hash des gerade laufenden main-Bundles IST die
// Version. Wir lesen ihn aus dem geladenen <script>, holen periodisch das frische
// index.html (no-store) und vergleichen den dort referenzierten Hash. Unterschied
// → neue Version live → Reload anbieten.

import { useState, useEffect, useRef } from 'react';

const MAIN_RE = /\/static\/js\/main\.([\w-]+)\.js/;

function runningBundleId() {
  if (typeof document === 'undefined') return null;
  for (const s of document.querySelectorAll('script[src]')) {
    const m = (s.getAttribute('src') || '').match(MAIN_RE);
    if (m) return m[1];
  }
  return null;
}

async function deployedBundleId() {
  try {
    const res = await fetch('/', { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(MAIN_RE);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * @returns {boolean} true, sobald eine neuere Version deployt ist als die laufende.
 */
export function useVersionCheck({ intervalMs = 5 * 60 * 1000 } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const current = useRef(null);

  useEffect(() => {
    current.current = runningBundleId();
    // Dev (kein gehashtes Bundle) → kein Check, kein Fehlalarm.
    if (!current.current) return undefined;

    let stopped = false;
    const check = async () => {
      if (stopped || updateAvailable) return;
      const dep = await deployedBundleId();
      if (!stopped && dep && current.current && dep !== current.current) {
        setUpdateAvailable(true);
      }
    };

    const iv = setInterval(check, intervalMs);
    const onVisible = () => { if (!document.hidden) check(); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    check(); // sofort beim Start prüfen

    return () => {
      stopped = true;
      clearInterval(iv);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // updateAvailable bewusst nicht in deps — einmal true, bleibt true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return updateAvailable;
}
